"use client";

import { Box, Text, VStack } from "@chakra-ui/react";
import { memo, useCallback, useMemo, useState } from "react";
import type {
  BuildQuestionnaireRequest,
  MindstringPrototype,
  SelectBySituationScope,
  SelectedThought,
  SelectedThoughtFree,
  Thought,
} from "@/generated/fastapi";
import type { QuestionnaireViewMode } from "@/shared/types";
import { useUserChoices } from "@/state/globalStore";
import {
  THOUGHT_QUERY_DEFAULTS,
  useBuildQuestionnaireQuery,
  useThoughtRefresh,
  useThoughtsQuery,
} from "@/features/thoughts";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericInteractiveGuidance from "@/shared/ui/display/GenericInteractiveGuidance";
import LoadingState from "@/shared/ui/LoadingState";
import QuestionnaireDimensionItem from "./QuestionnaireDimensionItem";
import QuestionnaireThoughItem from "./QuestionnaireToughtItem";
import type { QuestionnaireDimensionOriented } from "../types";
import {
  buildQuestionnaireDimensionOriented,
  buildQuestionnaireThoughtOriented,
} from "../utils";

function sortThoughtsByPrintOrder(
  thoughts: Thought[],
  selectedThoughtIds: string[],
): Thought[] {
  const thoughtMap = new Map(thoughts.map((thought) => [thought.id, thought]));
  const sortedThoughts: Thought[] = [];

  selectedThoughtIds.forEach((thoughtId) => {
    const thought = thoughtMap.get(thoughtId);
    if (thought) {
      sortedThoughts.push(thought);
    }
  });

  return sortedThoughts;
}

function getUniqueThoughtIds(
  selectedThoughtsFree: SelectedThoughtFree[],
  selectedThoughts: SelectedThought[],
): string[] {
  const orderedThoughtIds = selectedThoughtsFree.flatMap(
    (selection) => selection.thoughts_ids,
  );
  const seenThoughtIds = new Set(orderedThoughtIds);

  selectedThoughts.forEach((selection) => {
    selection.thoughts_ids.forEach((thoughtId) => {
      if (seenThoughtIds.has(thoughtId)) {
        return;
      }

      orderedThoughtIds.push(thoughtId);
      seenThoughtIds.add(thoughtId);
    });
  });

  return orderedThoughtIds;
}

function useQuestionnaireBuilder({
  situationId,
  situationScope = THOUGHT_QUERY_DEFAULTS.situationScope,
  prototype,
  limit,
}: {
  situationId?: string | null;
  situationScope?: SelectBySituationScope;
  prototype: MindstringPrototype;
  limit?: number;
}) {
  const { getSelectedThoughts, getSelectedThoughtsFree } = useUserChoices();
  const { resetReflectionsRefreshKey } = useThoughtRefresh();
  const selectedThoughts = useMemo(
    () =>
      situationId
        ? getSelectedThoughts(prototype, situationId)
        : getSelectedThoughts(prototype),
    [getSelectedThoughts, prototype, situationId],
  );
  const selectedThoughtsFree = useMemo(
    () => (situationId ? [] : getSelectedThoughtsFree(prototype)),
    [getSelectedThoughtsFree, prototype, situationId],
  );
  const selectedThoughtIds = useMemo(
    () => getUniqueThoughtIds(selectedThoughtsFree, selectedThoughts),
    [selectedThoughts, selectedThoughtsFree],
  );
  const request = useMemo<BuildQuestionnaireRequest>(
    () => ({
      situation_id: situationId ?? null,
      situation_scope: situationScope,
      prototype,
      selected_thoughts_ids:
        selectedThoughtIds.length > 0 ? selectedThoughtIds : null,
      limit,
    }),
    [limit, prototype, selectedThoughtIds, situationId, situationScope],
  );

  const questionnaireQuery = useBuildQuestionnaireQuery(request, {
    refreshKey: resetReflectionsRefreshKey,
    enabled: Boolean(situationId) || selectedThoughtIds.length > 0,
  });
  const selectedThoughtsQuery = useThoughtsQuery(
    {
      selected_ids: selectedThoughtIds,
      situation_scope: THOUGHT_QUERY_DEFAULTS.singleThoughtLookupSituationScope,
    },
    {
      enabled:
        !situationId &&
        selectedThoughtIds.length > 0 &&
        situationScope === THOUGHT_QUERY_DEFAULTS.situationScope,
    },
  );

  const thoughtsToReflect = useMemo(() => {
    const builtThoughts = questionnaireQuery.data?.thoughts ?? [];

    if (builtThoughts.length === 0) {
      return [];
    }

    if (selectedThoughtIds.length === 0) {
      return builtThoughts;
    }

    return sortThoughtsByPrintOrder(builtThoughts, selectedThoughtIds);
  }, [questionnaireQuery.data?.thoughts, selectedThoughtIds]);
  const hiddenUnassignedThoughtCount = useMemo(() => {
    if (selectedThoughtsQuery.isLoading || selectedThoughtsQuery.isError) {
      return 0;
    }

    return (selectedThoughtsQuery.data ?? []).filter(
      (thought) => !thought.situation_id,
    ).length;
  }, [
    selectedThoughtsQuery.data,
    selectedThoughtsQuery.isError,
    selectedThoughtsQuery.isLoading,
  ]);

  return {
    thoughtsToReflect,
    hiddenUnassignedThoughtCount,
    isLoading: questionnaireQuery.isLoading,
    isError: questionnaireQuery.isError,
  };
}

function useOptimisticAnsweredKeys(thoughts: Thought[]) {
  const sourceKey = useMemo(
    () => thoughts.map((thought) => thought.id).join("|"),
    [thoughts],
  );
  const [optimisticState, setOptimisticState] = useState<{
    sourceKey: string;
    answeredKeys: string[];
  }>({
    sourceKey: "",
    answeredKeys: [],
  });

  const answeredKeys = useMemo(
    () =>
      optimisticState.sourceKey === sourceKey
        ? optimisticState.answeredKeys
        : [],
    [optimisticState.answeredKeys, optimisticState.sourceKey, sourceKey],
  );

  const answeredSet = useMemo(() => new Set(answeredKeys), [answeredKeys]);

  const handleAnswerProvided = useCallback(
    (thoughtId: string, answeredDimension: string) => {
      const answeredKey = `${thoughtId}:${answeredDimension}`;

      setOptimisticState((previousState) => {
        const currentAnsweredKeys =
          previousState.sourceKey === sourceKey
            ? previousState.answeredKeys
            : [];

        if (
          previousState.sourceKey === sourceKey &&
          currentAnsweredKeys.includes(answeredKey)
        ) {
          return previousState;
        }

        return {
          sourceKey,
          answeredKeys: [...currentAnsweredKeys, answeredKey],
        };
      });
    },
    [sourceKey],
  );

  return { answeredSet, handleAnswerProvided };
}

function useOptimisticQuestionnaireThought(thoughts: Thought[]) {
  const { answeredSet, handleAnswerProvided } =
    useOptimisticAnsweredKeys(thoughts);

  const questionnaireThoughts = useMemo(
    () =>
      thoughts
        .map((thought) => buildQuestionnaireThoughtOriented(thought))
        .map((questionnaireThought) => ({
          ...questionnaireThought,
          dimensionQuestions: questionnaireThought.dimensionQuestions.filter(
            (question) =>
              !answeredSet.has(
                `${questionnaireThought.id}:${question.dimension}`,
              ),
          ),
        }))
        .filter(
          (questionnaireThought) =>
            questionnaireThought.dimensionQuestions.length > 0,
        ),
    [thoughts, answeredSet],
  );

  return { questionnaireThoughts, handleAnswerProvided };
}

function useOptimisticQuestionnaireDimension(thoughts: Thought[]) {
  const { answeredSet, handleAnswerProvided } =
    useOptimisticAnsweredKeys(thoughts);

  const dimensionQuestionnaire = useMemo<QuestionnaireDimensionOriented[]>(
    () =>
      buildQuestionnaireDimensionOriented(thoughts)
        .map((dimension) => ({
          ...dimension,
          thoughts: dimension.thoughts.filter(
            (thought) =>
              !answeredSet.has(`${thought.id}:${dimension.dimension}`),
          ),
        }))
        .filter((dimension) => dimension.thoughts.length > 0),
    [thoughts, answeredSet],
  );

  return { dimensionQuestionnaire, handleAnswerProvided };
}

export interface ReflectQuestionnaireProps {
  situationId?: string | null;
  situationScope?: SelectBySituationScope;
  prototype: MindstringPrototype;
  limit?: number;
  viewMode: QuestionnaireViewMode;
}

export type ReflectQuestionnaireContentProps = {
  thoughtsToReflect: Thought[];
  viewMode: QuestionnaireViewMode;
  emptyTitle?: string;
  emptyMessage?: string;
  completeTitle?: string;
  completeMessage?: string;
  showCompleteState?: boolean;
};

export function ReflectQuestionnaireContent({
  thoughtsToReflect,
  viewMode,
  emptyTitle = "All selected thoughts are reflected",
  emptyMessage = "There are no remaining prompts for the selected thoughts.",
  completeTitle = "Thought selection is fully reflected",
  completeMessage = "All reflection prompts for the selected thoughts are complete.",
  showCompleteState = true,
}: ReflectQuestionnaireContentProps) {
  const thoughtsForThoughtView = useMemo(
    () => (viewMode === "thought-oriented" ? thoughtsToReflect : []),
    [viewMode, thoughtsToReflect],
  );
  const thoughtsForDimensionView = useMemo(
    () => (viewMode === "dimension-oriented" ? thoughtsToReflect : []),
    [viewMode, thoughtsToReflect],
  );

  const { questionnaireThoughts, handleAnswerProvided: handleThoughtAnswer } =
    useOptimisticQuestionnaireThought(thoughtsForThoughtView);
  const {
    dimensionQuestionnaire,
    handleAnswerProvided: handleDimensionAnswer,
  } = useOptimisticQuestionnaireDimension(thoughtsForDimensionView);

  if (thoughtsToReflect.length === 0) {
    return (
      <GenericEmptyComponent
        title={emptyTitle}
        message={emptyMessage}
        variant="inline"
      />
    );
  }

  const hasContent =
    viewMode === "thought-oriented"
      ? questionnaireThoughts.length > 0
      : dimensionQuestionnaire.length > 0;

  if (!hasContent) {
    if (!showCompleteState) {
      return null;
    }

    return (
      <GenericEmptyComponent
        title={completeTitle}
        message={completeMessage}
        variant="inline"
        icon="chat"
      />
    );
  }

  return (
    <Box>
      <VStack align="stretch" spaceY="2">
        {viewMode === "thought-oriented" &&
          questionnaireThoughts.map((thought) => (
            <Box
              key={thought.id}
              p="2"
              border="sm"
              borderColor="moveon.inactive"
              borderRadius="md"
              width="full"
            >
              <Text fontWeight="bold" mb="2">
                {thought.mindstring}
              </Text>
              <VStack align="stretch">
                {thought.dimensionQuestions.map((question) => (
                  <QuestionnaireThoughItem
                    key={`${thought.id}-${question.dimension}`}
                    question={question}
                    thought={thought}
                    onAnswerProvided={handleThoughtAnswer}
                  />
                ))}
              </VStack>
            </Box>
          ))}

        {viewMode === "dimension-oriented" &&
          dimensionQuestionnaire.map((dimension) => (
            <Box
              key={dimension.dimension}
              p="2"
              border="sm"
              borderColor="moveon.inactive"
              borderRadius="md"
              width="full"
            >
              <Text fontWeight="bold" mb="2">
                {dimension.question}
              </Text>
              <QuestionnaireDimensionItem
                dimensionData={dimension}
                onAnswerProvided={handleDimensionAnswer}
              />
            </Box>
          ))}
      </VStack>
    </Box>
  );
}

function ReflectQuestionnaireInner({
  situationId,
  situationScope,
  prototype,
  limit = 10,
  viewMode,
}: ReflectQuestionnaireProps) {
  const {
    thoughtsToReflect,
    hiddenUnassignedThoughtCount,
    isLoading,
    isError,
  } = useQuestionnaireBuilder({
    situationId,
    situationScope,
    prototype,
    limit,
  });

  const unassignedThoughtGuidance =
    hiddenUnassignedThoughtCount > 0 ? (
      <GenericInteractiveGuidance
        title={
          hiddenUnassignedThoughtCount === 1
            ? "1 selected thought is not listed"
            : `${hiddenUnassignedThoughtCount} selected thoughts are not listed`
        }
        message="Unassigned thoughts must be anchored to a situation before they can be reflected."
        compactMessage="Anchor unassigned thoughts before reflecting them."
        icon="assign"
        accentColor="moveon.warning"
        accentBg="moveon.warningBg"
        accentBorderColor="moveon.warningBorder"
      />
    ) : null;

  if (thoughtsToReflect.length === 0 && !isLoading) {
    return (
      <VStack align="stretch" gap="3">
        {unassignedThoughtGuidance}
        <GenericEmptyComponent
          title="All selected thoughts are reflected"
          message="There are no remaining prompts for the selected thoughts."
          variant="inline"
        />
      </VStack>
    );
  }

  if (isLoading) {
    return (
      <LoadingState
        message="Fetching thoughts to reflect..."
        variant="inline"
      />
    );
  }

  if (isError) {
    return (
      <GenericEmptyComponent
        title="Could not load reflections"
        message="Try again in a moment."
        variant="inline"
      />
    );
  }

  return (
    <VStack align="stretch" gap="3">
      {unassignedThoughtGuidance}
      <ReflectQuestionnaireContent
        thoughtsToReflect={thoughtsToReflect}
        viewMode={viewMode}
      />
    </VStack>
  );
}

export default memo(ReflectQuestionnaireInner);
